import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { trackEvent } from "@/lib/analytics";
import { LandingProvider, type LandingConfig } from "./LandingContext";
import { ParentsLandingSEO } from "./ParentsLandingSEO";
import { LandingNav, LandingFooter } from "./LandingChrome";
import { StickyCta } from "./StickyCta";
import { ParentsHero } from "./ParentsHero";
import {
  ParentsTestimonials,
  ParentsMidCta,
  ParentsHowItWorks,
  ParentsWhy,
  ParentsSecondCta,
  ParentsFaq,
} from "./ParentsSections";

/**
 * Reusable Meta-ads landing page. Single goal: convert cold ad traffic into
 * App Store clicks. Minimal nav, short and emotional. The audience is entirely
 * driven by the passed config (copy, paths, hero image, analytics id), so the
 * same layout serves multiple campaigns (families, home-reno, …).
 */
export function LandingPage({ config }: { config: LandingConfig }) {
  const { language } = useLanguage();

  // Fire a landing-view event so visits can be measured against App Store clicks.
  useEffect(() => {
    trackEvent("landing_view", { page: config.analyticsPage, language });
  }, [language, config.analyticsPage]);

  return (
    <LandingProvider config={config}>
      {/* landing-light scopes the cream sociallite-style theme to this page only */}
      <div className="landing-light min-h-screen bg-background text-foreground">
        <ParentsLandingSEO />
        <LandingNav />
        <main>
          <ParentsHero />
          <ParentsTestimonials />
          <ParentsMidCta />
          <ParentsHowItWorks />
          <ParentsWhy />
          <ParentsSecondCta />
          <ParentsFaq />
        </main>
        <LandingFooter />
        {/* Spacer so the sticky mobile CTA never covers the footer */}
        <div className="h-20 lg:hidden" aria-hidden="true" />
        <StickyCta />
      </div>
    </LandingProvider>
  );
}
